import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  createStyles,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core'
import { RoleHierarchySelector } from './roleHierarchySelector'
import {
  DEFAULT_ROLE_HIERARCHY,
  RoleHierarchy,
} from '../../../shared/src/data/roleHierarchy/roleHierarchy'
import { useRoleHierarchyManager } from './queries/roleHierarchyManagement'
import { UserRoleSelector } from './userRoleSelector'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
  }),
)

export function RoleHierarchyChanger({
  roleHierarchies,
}: {
  roleHierarchies: RoleHierarchy[]
}): JSX.Element {
  const {
    getUserRoleHierarchyQuery: {
      data: userCurrentRoleHierarchy,
      isLoading: isLoadingUserCurrentRoleHierarchy,
      error: userCurrentRoleHierarchyError,
    },
    updateUserRoleHierarchyMutation: { mutateAsync: updateUserRoleHierarchy },
  } = useRoleHierarchyManager()
  const classes = useStyles()
  const [resultMessage, setResultMessage] = useState<{
    message: string
    type: 'error' | 'success'
  } | null>(null)
  const [selectedRoleHierarchy, setSelectedRoleHierarchy] =
    useState<RoleHierarchy>()

  useEffect(() => {
    setSelectedRoleHierarchy(userCurrentRoleHierarchy)
  }, [userCurrentRoleHierarchy])

  const handleUpdateButtonClicked = useCallback(async () => {
    setResultMessage(null)
    try {
      await updateUserRoleHierarchy(selectedRoleHierarchy!)
      setResultMessage({
        message: 'Updated user role hierarchy successfully.',
        type: 'success',
      })
    } catch (error) {
      setResultMessage({
        message: 'Failed updating user role hierarchy.',
        type: 'error',
      })
    }
  }, [updateUserRoleHierarchy, selectedRoleHierarchy])

  const areRoleHierarchiesEqual = useCallback(
    (a1, a2) =>
      a1?.organization.id === a2.organization.id &&
      a1.branch.id === a2.branch.id &&
      a1.role === a2.role,
    [],
  )

  if (isLoadingUserCurrentRoleHierarchy)
    return <Typography>Loading user hierarchy</Typography>
  if (!!userCurrentRoleHierarchyError)
    return <Typography>Error! {userCurrentRoleHierarchyError}</Typography>

  return (
    <div className={classes.root}>
      <RoleHierarchySelector
        roleHierarchies={roleHierarchies}
        selectedRoleHierarchy={selectedRoleHierarchy}
        setSelectedRoleHierarchy={(roleHierarchy) => {
          setSelectedRoleHierarchy((prev) => ({ ...prev, ...roleHierarchy }))
        }}
      />
      <UserRoleSelector
        disabled={!selectedRoleHierarchy?.branch?.id}
        selectedRole={selectedRoleHierarchy?.role}
        setSelectedRole={(role) => {
          setSelectedRoleHierarchy((prev) => ({
            ...(prev || DEFAULT_ROLE_HIERARCHY),
            role,
          }))
        }}
      />
      <Button
        style={{ marginTop: '16px' }}
        variant='contained'
        color='primary'
        disabled={
          !selectedRoleHierarchy ||
          areRoleHierarchiesEqual(
            selectedRoleHierarchy,
            userCurrentRoleHierarchy,
          )
        }
        onClick={handleUpdateButtonClicked}
      >
        Update
      </Button>
      {!!resultMessage && (
        <Typography
          color={resultMessage!.type === 'error' ? 'error' : 'textPrimary'}
        >
          {resultMessage.message}
        </Typography>
      )}
    </div>
  )
}
